// modal.ts

class Modal {
    private modals: NodeListOf<HTMLElement>;
    private closeButtons: NodeListOf<HTMLElement>;
    private currentModal: HTMLElement | null = null;
    private currentModalHost: HTMLElement | null = null;
    private movedModalParent: ParentNode | null = null;
    private movedModalNextSibling: ChildNode | null = null;
    private savedScrollY: number = 0;
    private isScrollLocked: boolean = false;

    constructor() {
        this.modals = document.querySelectorAll('.modal');
        this.closeButtons = document.querySelectorAll('.close-m');

        // Инициализация
        this._init();
    }

    private lockScroll(): void {
        if (this.isScrollLocked) return;
        this.savedScrollY = window.scrollY;
        document.body.style.cssText = `overflow: hidden;`;
        this.isScrollLocked = true;
    }

    private unlockScroll(): void {
        if (!this.isScrollLocked) return;
        document.body.style.cssText = "";
        window.scrollTo(0, this.savedScrollY);
        this.isScrollLocked = false;
    }

    private checkTableScroll(modal: HTMLElement): void {
        const modalTable = modal.querySelector('.modal-table') as HTMLElement;
        if (modalTable) {
            const hasScroll = modalTable.scrollHeight > modalTable.clientHeight;
            if (hasScroll) {
                modalTable.classList.add('has-scroll');
            } else {
                modalTable.classList.remove('has-scroll');
            }
        }
    }

    private getActiveTab(element: HTMLElement): HTMLElement | null {
        const tab = element.closest('.catalog__cruise-tab') as HTMLElement;
        if (tab) {
            return tab;
        }

        const tabs = document.querySelectorAll('.catalog__cruise-tab');
        for (let i = 0; i < tabs.length; i++) {
            const tabElement = tabs[i] as HTMLElement;
            const style = window.getComputedStyle(tabElement);
            if (style.display !== 'none' && tabElement.offsetParent !== null) {
                return tabElement;
            }
        }
        
        return null;
    }

    private findModalInAncestors(trigger: HTMLElement, modalId: string): HTMLElement | null {
        let current: HTMLElement | null = trigger;

        while (current) {
            const scopedModal = current.querySelector(`.modal[id="${modalId}"]`) as HTMLElement | null;
            if (scopedModal) {
                return scopedModal;
            }
            current = current.parentElement;
        }

        return null;
    }

    private setModalHostState(modal: HTMLElement | null, isOpen: boolean): void {
        if (!modal) return;
        const host = modal.closest('.detailc-infoblock.hidden-mobile') as HTMLElement | null;
        if (!host) return;
        host.classList.toggle('modal-host-open', isOpen);
        this.currentModalHost = isOpen ? host : null;
    }

    private moveModalToBodyIfNeeded(modal: HTMLElement): void {
        const host = modal.closest('.placing-item');
        if (!host || modal.parentElement === document.body) return;

        this.movedModalParent = modal.parentNode;
        this.movedModalNextSibling = modal.nextSibling;
        document.body.appendChild(modal);
    }

    private restoreMovedModalIfNeeded(): void {
        if (!this.currentModal || !this.movedModalParent) return;

        if (this.movedModalNextSibling && this.movedModalNextSibling.parentNode === this.movedModalParent) {
            this.movedModalParent.insertBefore(this.currentModal, this.movedModalNextSibling);
        } else {
            this.movedModalParent.appendChild(this.currentModal);
        }

        this.movedModalParent = null;
        this.movedModalNextSibling = null;
    }

    private _init(): void {
        // Делегирование событий для открытия модалок
        document.addEventListener('click', (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const button = target.closest('.open-modal') as HTMLElement | null;
            
            if (button) {
                // Для action-tooltip-btn проверяем ширину экрана
                if (button.classList.contains('discount-tooltip-btn') && window.innerWidth > 990) {
                    return;
                }
                
                e.preventDefault();
                const modalId = button.getAttribute('data-modal');
                if (modalId) {
                    // Находим активный таб, в котором находится кнопка
                    const activeTab = this.getActiveTab(button);
                    const scopedContainer = activeTab || button;
                    this.open(modalId, scopedContainer);
                }
            }
        });

        // Закрытие модалки по кнопке-крестику
        this.closeButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => {
                this.close();
            });
        });

        // Закрытие модалки по клику вне нее
        this.modals.forEach((modal: HTMLElement) => {
            modal.addEventListener('click', (e: MouseEvent) => {
                if (e.target === modal) {
                    this.close();
                }
            });
        });

        // Закрытие модалки по нажатию клавиши Escape
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.close();
            }
        });
    }

    // Открытие модалки по ID с учетом контекста таба
    public open(modalId: string, container: HTMLElement | null = null): void {
        // Закрываем все модалки перед открытием новой
        this.close();

        let modal: HTMLElement | null = null;

        if (container) {
            modal = this.findModalInAncestors(container, modalId);
        }

        if (!modal && container) {
            modal = container.querySelector(`.modal[id="${modalId}"]`) as HTMLElement | null;
        }

        if (!modal) {
            modal = document.getElementById(modalId);
        }
        
        if (modal) {
            this.setModalHostState(modal, true);
            this.moveModalToBodyIfNeeded(modal);
            modal.classList.add('show');
            document.body.classList.add('modal-open');
            this.currentModal = modal;
            // Блокируем скролл при открытии модалки
            this.lockScroll();
            
            // Проверяем, заполнена ли таблица (есть ли скролл)
            this.checkTableScroll(modal);
        }
    }

    // Закрытие всех модалок
    public close(): void {
        this.modals.forEach((modal: HTMLElement) => {
            modal.classList.remove('show');
        });
        document.body.classList.remove('modal-open');
        if (this.currentModalHost) {
            this.currentModalHost.classList.remove('modal-host-open');
            this.currentModalHost = null;
        }
        this.restoreMovedModalIfNeeded();
        this.currentModal = null;
        // Разблокируем скролл при закрытии модалки
        this.unlockScroll();
    }
}

// Экспортируем модалку для использования в других частях приложения
export const modal = new Modal();
(window as any).modal = modal;
